import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/query/query-keys';
import type {
  AccommodationExpense,
  TransportExpenseData,
  FuelExpenseData,
} from '@/components/trips/form/ExpensesForm';
import type {
  TripCityDetail,
  TripCityFuel,
  TripCityHotel,
  TripCityTransportation,
  TransportationAssistanceType,
} from '@/hooks/trips/types';

interface HotelPayload {
  business_trip_city_id: number;
  check_in_date: string;
  check_out_date: string;
  booking_support: boolean;
  prepayment_amount?: number;
}

interface TransportPayload {
  business_trip_city_id: number;
  assistance_type: TransportationAssistanceType;
  booking_support: boolean;
  amount?: number;
}

type TransportUpdatePayload = Omit<TransportPayload, 'business_trip_city_id'>;

interface FuelPayload {
  business_trip_city_id: number;
  fuel_type_id: number;
  fuel_amount: number;
  car_model?: string;
}

function parseAssistanceType(value: unknown): TransportationAssistanceType | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  if (value === 'ticket_or_advance' || value === 'rental_car' || value === 'car_with_driver') {
    return value;
  }

  return undefined;
}

function parseDate(value: unknown): Date {
  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    try {
      return parseISO(value);
    } catch {
      return new Date(value);
    }
  }

  return new Date();
}

function mapHotelResponse(raw: unknown): AccommodationExpense {
  const record = (raw ?? {}) as Record<string, unknown>;

  return {
    id: Number(record.id ?? 0),
    checkInDate: parseDate(record.check_in_date),
    checkOutDate: parseDate(record.check_out_date),
    totalAmount: String(record.total_amount ?? record.prepayment_amount ?? 0),
    currency: 'GEL',
    needsBookingHelp: Boolean(record.booking_support),
  };
}

function mapTransportationResponse(
  raw: unknown,
  fallbackDates?: {
    arrivalDate?: Date;
    departureDate?: Date;
  }
): TransportExpenseData {
  const record = (raw ?? {}) as Record<string, unknown>;
  const assistanceType = parseAssistanceType(record.assistance_type) ?? 'ticket_or_advance';
  const rawAmount = Number(record.amount ?? record.prepayment_amount ?? Number.NaN);
  const amount = Number.isFinite(rawAmount) && rawAmount > 0 ? String(rawAmount) : '';

  return {
    id: Number(record.id ?? 0),
    amount,
    currency: 'GEL',
    assistanceType,
    needsBookingSupport: Boolean(record.booking_support),
    arrivalDate: fallbackDates?.arrivalDate,
    departureDate: fallbackDates?.departureDate,
  };
}

function mapFuelResponse(raw: unknown): FuelExpenseData {
  const record = (raw ?? {}) as Record<string, unknown>;

  return {
    id: Number(record.id ?? 0),
    fuelTypeId: String(record.fuel_type_id ?? ''),
    fuelTypeName: String(record.fuel_type_name ?? ''),
    carModel: typeof record.car_model === 'string' ? record.car_model : '',
    amount: String(record.fuel_amount ?? 0),
    currency: 'GEL',
  };
}

function mapHotelDetail(raw: unknown): TripCityHotel | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const record = raw as Record<string, unknown>;

  return {
    id: Number(record.id ?? 0),
    checkInDate: String(record.check_in_date ?? ''),
    checkOutDate: String(record.check_out_date ?? ''),
    bookingSupport: Boolean(record.booking_support),
    isBooked: Boolean(record.is_booked),
    prepaymentAmount: Number(record.prepayment_amount ?? 0),
    prepaymentCurrencyId: Number(record.prepayment_currency_id ?? 0),
    prepaymentCurrencyName: String(record.prepayment_currency_name ?? ''),
    prepaymentDate: typeof record.prepayment_date === 'string' ? record.prepayment_date : undefined,
    contractedHotel: Boolean(record.contracted_hotel),
    dailyAmount: Number(record.daily_amount ?? 0),
    daysCount: Number(record.days_count ?? 0),
    totalAmount: Number(record.total_amount ?? record.prepayment_amount ?? 0),
  };
}

function mapTransportationDetail(raw: unknown): TripCityTransportation | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const assistanceType = parseAssistanceType(record.assistance_type);
  const assistanceTypeLabel =
    typeof record.assistance_type_label === 'string' ? record.assistance_type_label : undefined;

  return {
    id: Number(record.id ?? 0),
    companyName: String(record.company_name ?? ''),
    bookingSupport: Boolean(record.booking_support),
    isBooked: Boolean(record.is_booked),
    prepaymentAmount: Number(record.prepayment_amount ?? 0),
    prepaymentCurrencyId: Number(record.prepayment_currency_id ?? 0),
    prepaymentCurrencyName: String(record.prepayment_currency_name ?? ''),
    prepaymentDate: typeof record.prepayment_date === 'string' ? record.prepayment_date : undefined,
    amount: Number(record.amount ?? record.prepayment_amount ?? 0),
    assistanceType,
    assistanceTypeLabel,
  };
}

function mapFuelDetail(raw: unknown): TripCityFuel | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const record = raw as Record<string, unknown>;

  return {
    id: Number(record.id ?? 0),
    fuelTypeId: Number(record.fuel_type_id ?? 0),
    fuelTypeName: String(record.fuel_type_name ?? ''),
    carModel: typeof record.car_model === 'string' ? record.car_model : undefined,
    fuelAmount: Number(record.fuel_amount ?? 0),
  };
}

interface CityExpenseMutations {
  createHotel: (expense: AccommodationExpense) => Promise<AccommodationExpense>;
  updateHotel: (hotelId: number, expense: AccommodationExpense) => Promise<AccommodationExpense>;
  deleteHotel: (hotelId: number) => Promise<void>;
  createTransportation: (expense: TransportExpenseData) => Promise<TransportExpenseData>;
  updateTransportation: (
    transportationId: number,
    expense: TransportExpenseData
  ) => Promise<TransportExpenseData>;
  deleteTransportation: (transportationId: number) => Promise<void>;
  createFuel: (expense: FuelExpenseData) => Promise<FuelExpenseData>;
  updateFuel: (fuelId: number, expense: FuelExpenseData) => Promise<FuelExpenseData>;
  deleteFuel: (fuelId: number) => Promise<void>;
}

export function useCityExpenses(tripId: string, cityId?: number): CityExpenseMutations {
  const queryClient = useQueryClient();

  if (!tripId) {
    throw new Error('Trip ID is required to manage city expenses.');
  }

  const updateCityCache = (
    cityIdentifier: number,
    updater: (detail: TripCityDetail) => TripCityDetail
  ) => {
    queryClient.setQueryData(
      queryKeys.tripCity(tripId, cityIdentifier),
      (current: TripCityDetail | undefined) => {
        if (!current) {
          return current;
        }
        return updater(current);
      }
    );
  };

  const invalidateCity = (cityIdentifier: number) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.trip(tripId) });
    queryClient.invalidateQueries({
      queryKey: queryKeys.tripCities(tripId),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.tripCity(tripId, cityIdentifier),
    });
  };

  const ensureCityId = () => {
    if (cityId === undefined) {
      throw new Error('City ID is required before modifying expenses.');
    }
    return cityId;
  };

  const createHotel = async (expense: AccommodationExpense): Promise<AccommodationExpense> => {
    const resolvedCityId = ensureCityId();

    const payload: HotelPayload = {
      business_trip_city_id: resolvedCityId,
      check_in_date: format(expense.checkInDate, 'yyyy-MM-dd'),
      check_out_date: format(expense.checkOutDate, 'yyyy-MM-dd'),
      booking_support: expense.needsBookingHelp,
      prepayment_amount: Number(expense.totalAmount ?? 0),
    };

    const response = await apiFetch('/api/v1/business-trip-hotels', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const body = await response.json().catch(() => null);

    if (!response.ok) {
      const message =
        body && typeof body === 'object' && 'error' in (body as Record<string, unknown>)
          ? String(
              (body as Record<string, { message?: string }>).error?.message ??
                'Failed to create hotel expense'
            )
          : 'Failed to create hotel expense';
      throw new Error(message);
    }

    const mapped = mapHotelResponse(body);
    const hotelDetail = mapHotelDetail(body);

    if (hotelDetail) {
      updateCityCache(resolvedCityId, (detail) => {
        const hotels = detail.hotels.filter((item) => item.id !== hotelDetail.id);
        hotels.push(hotelDetail);
        return {
          ...detail,
          hotels,
        };
      });
    }

    invalidateCity(resolvedCityId);
    return mapped;
  };

  const updateHotel = async (
    hotelId: number,
    expense: AccommodationExpense
  ): Promise<AccommodationExpense> => {
    const resolvedCityId = ensureCityId();

    const payload: Partial<HotelPayload> = {
      check_in_date: format(expense.checkInDate, 'yyyy-MM-dd'),
      check_out_date: format(expense.checkOutDate, 'yyyy-MM-dd'),
      booking_support: expense.needsBookingHelp,
      prepayment_amount: Number(expense.totalAmount ?? 0),
    };

    const response = await apiFetch(`/api/v1/business-trip-hotels/${hotelId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const body = await response.json().catch(() => null);

    if (!response.ok) {
      const message =
        body && typeof body === 'object' && 'error' in (body as Record<string, unknown>)
          ? String(
              (body as Record<string, { message?: string }>).error?.message ??
                'Failed to update hotel expense'
            )
          : 'Failed to update hotel expense';
      throw new Error(message);
    }

    const mapped = mapHotelResponse(body);
    const hotelDetail = mapHotelDetail(body);

    if (hotelDetail) {
      updateCityCache(resolvedCityId, (detail) => {
        const hotels = detail.hotels.map((item) => (item.id === hotelId ? hotelDetail : item));
        return {
          ...detail,
          hotels,
        };
      });
    }

    invalidateCity(resolvedCityId);
    return mapped;
  };

  const deleteHotel = async (hotelId: number): Promise<void> => {
    const resolvedCityId = ensureCityId();

    const response = await apiFetch(`/api/v1/business-trip-hotels/${hotelId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const message =
        body && typeof body === 'object' && 'error' in (body as Record<string, unknown>)
          ? String(
              (body as Record<string, { message?: string }>).error?.message ??
                'Failed to delete hotel expense'
            )
          : 'Failed to delete hotel expense';
      throw new Error(message);
    }

    updateCityCache(resolvedCityId, (detail) => ({
      ...detail,
      hotels: detail.hotels.filter((hotel) => hotel.id !== hotelId),
    }));

    invalidateCity(resolvedCityId);
  };

  const createTransportation = async (
    expense: TransportExpenseData
  ): Promise<TransportExpenseData> => {
    const resolvedCityId = ensureCityId();
    const assistanceType = expense.assistanceType ?? 'ticket_or_advance';
    const normalizedAmount = expense.amount?.trim();

    // Only ticket_or_advance uses booking_support and amount fields
    // For other types, explicitly send default values to clear any previously set values
    const isTicketOrAdvance = assistanceType === 'ticket_or_advance';
    const bookingSupport = isTicketOrAdvance ? expense.needsBookingSupport : false;
    const amount = isTicketOrAdvance && normalizedAmount ? Number(normalizedAmount) : 0;

    const payload: TransportPayload = {
      business_trip_city_id: resolvedCityId,
      assistance_type: assistanceType,
      booking_support: bookingSupport,
      amount,
    };

    const response = await apiFetch('/api/v1/business-trip-transportations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const body = await response.json().catch(() => null);

    if (!response.ok) {
      const message =
        body && typeof body === 'object' && 'error' in (body as Record<string, unknown>)
          ? String(
              (body as Record<string, { message?: string }>).error?.message ??
                'Failed to create transportation expense'
            )
          : 'Failed to create transportation expense';
      throw new Error(message);
    }

    const mapped = mapTransportationResponse(body, {
      arrivalDate: expense.arrivalDate,
      departureDate: expense.departureDate,
    });
    const transportationDetail = mapTransportationDetail(body);

    if (transportationDetail) {
      updateCityCache(resolvedCityId, (detail) => {
        const transportations = detail.transportations.filter(
          (item) => item.id !== transportationDetail.id
        );
        transportations.push(transportationDetail);
        return {
          ...detail,
          transportations,
        };
      });
    }

    invalidateCity(resolvedCityId);
    return mapped;
  };

  const updateTransportation = async (
    transportationId: number,
    expense: TransportExpenseData
  ): Promise<TransportExpenseData> => {
    const resolvedCityId = ensureCityId();

    const assistanceType = expense.assistanceType ?? 'ticket_or_advance';
    const normalizedAmount = expense.amount?.trim();

    // Only ticket_or_advance uses booking_support and amount fields
    // For other types, explicitly send default values to clear any previously set values
    const isTicketOrAdvance = assistanceType === 'ticket_or_advance';
    const bookingSupport = isTicketOrAdvance ? expense.needsBookingSupport : false;
    const amount = isTicketOrAdvance && normalizedAmount ? Number(normalizedAmount) : 0;

    const payload: TransportUpdatePayload = {
      assistance_type: assistanceType,
      booking_support: bookingSupport,
      amount,
    };

    const response = await apiFetch(`/api/v1/business-trip-transportations/${transportationId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const body = await response.json().catch(() => null);

    if (!response.ok) {
      const message =
        body && typeof body === 'object' && 'error' in (body as Record<string, unknown>)
          ? String(
              (body as Record<string, { message?: string }>).error?.message ??
                'Failed to update transportation expense'
            )
          : 'Failed to update transportation expense';
      throw new Error(message);
    }

    const mapped = mapTransportationResponse(body, {
      arrivalDate: expense.arrivalDate,
      departureDate: expense.departureDate,
    });
    const transportationDetail = mapTransportationDetail(body);

    if (transportationDetail) {
      updateCityCache(resolvedCityId, (detail) => {
        const transportations = detail.transportations.map((item) =>
          item.id === transportationId ? transportationDetail : item
        );
        return {
          ...detail,
          transportations,
        };
      });
    }

    invalidateCity(resolvedCityId);
    return mapped;
  };

  const deleteTransportation = async (transportationId: number): Promise<void> => {
    const resolvedCityId = ensureCityId();

    const response = await apiFetch(`/api/v1/business-trip-transportations/${transportationId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const message =
        body && typeof body === 'object' && 'error' in (body as Record<string, unknown>)
          ? String(
              (body as Record<string, { message?: string }>).error?.message ??
                'Failed to delete transportation expense'
            )
          : 'Failed to delete transportation expense';
      throw new Error(message);
    }

    updateCityCache(resolvedCityId, (detail) => ({
      ...detail,
      transportations: detail.transportations.filter(
        (transport) => transport.id !== transportationId
      ),
    }));

    invalidateCity(resolvedCityId);
  };

  const createFuel = async (expense: FuelExpenseData): Promise<FuelExpenseData> => {
    const resolvedCityId = ensureCityId();

    const payload: FuelPayload = {
      business_trip_city_id: resolvedCityId,
      fuel_type_id: Number(expense.fuelTypeId),
      fuel_amount: Number(expense.amount ?? 0),
      car_model: expense.carModel || undefined,
    };

    const response = await apiFetch('/api/v1/business-trip-fuels', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const body = await response.json().catch(() => null);

    if (!response.ok) {
      const message =
        body && typeof body === 'object' && 'error' in (body as Record<string, unknown>)
          ? String(
              (body as Record<string, { message?: string }>).error?.message ??
                'Failed to create fuel expense'
            )
          : 'Failed to create fuel expense';
      throw new Error(message);
    }

    const mapped = mapFuelResponse(body);
    const fuelDetail = mapFuelDetail(body);

    if (fuelDetail) {
      updateCityCache(resolvedCityId, (detail) => {
        const fuels = detail.fuels.filter((item) => item.id !== fuelDetail.id);
        fuels.push(fuelDetail);
        return {
          ...detail,
          fuels,
        };
      });
    }

    invalidateCity(resolvedCityId);
    return mapped;
  };

  const updateFuel = async (fuelId: number, expense: FuelExpenseData): Promise<FuelExpenseData> => {
    const resolvedCityId = ensureCityId();

    const payload: Partial<FuelPayload> = {
      fuel_type_id: Number(expense.fuelTypeId),
      fuel_amount: Number(expense.amount ?? 0),
      car_model: expense.carModel || undefined,
    };

    const response = await apiFetch(`/api/v1/business-trip-fuels/${fuelId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const body = await response.json().catch(() => null);

    if (!response.ok) {
      const message =
        body && typeof body === 'object' && 'error' in (body as Record<string, unknown>)
          ? String(
              (body as Record<string, { message?: string }>).error?.message ??
                'Failed to update fuel expense'
            )
          : 'Failed to update fuel expense';
      throw new Error(message);
    }

    const mapped = mapFuelResponse(body);
    const fuelDetail = mapFuelDetail(body);

    if (fuelDetail) {
      updateCityCache(resolvedCityId, (detail) => {
        const fuels = detail.fuels.map((item) => (item.id === fuelId ? fuelDetail : item));
        return {
          ...detail,
          fuels,
        };
      });
    }

    invalidateCity(resolvedCityId);
    return mapped;
  };

  const deleteFuel = async (fuelId: number): Promise<void> => {
    const resolvedCityId = ensureCityId();

    const response = await apiFetch(`/api/v1/business-trip-fuels/${fuelId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const message =
        body && typeof body === 'object' && 'error' in (body as Record<string, unknown>)
          ? String(
              (body as Record<string, { message?: string }>).error?.message ??
                'Failed to delete fuel expense'
            )
          : 'Failed to delete fuel expense';
      throw new Error(message);
    }

    updateCityCache(resolvedCityId, (detail) => ({
      ...detail,
      fuels: detail.fuels.filter((fuel) => fuel.id !== fuelId),
    }));

    invalidateCity(resolvedCityId);
  };

  return {
    createHotel,
    updateHotel,
    deleteHotel,
    createTransportation,
    updateTransportation,
    deleteTransportation,
    createFuel,
    updateFuel,
    deleteFuel,
  };
}
